// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Link} from "react-router-dom";
import {Button, DatePicker, Modal, Spin, Table, Tag, message} from "antd";
import moment from "moment";
import * as Setting from "./Setting";
import * as ProviderBackend from "./backend/ProviderBackend";
import * as Provider from "./auth/Provider";
import i18next from "i18next";
import BaseListPage from "./BaseListPage";
import PopconfirmModal from "./common/modal/PopconfirmModal";

class ProviderListPage extends BaseListPage {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      filters: undefined,
      sorter: undefined,
      profitModalVisible: false,
      profitLoading: false,
      profitDoing: false,
      profitStatus: null,
      selectedProvider: null,
      paymentStatusLoading: {},
      now: Date.now(),
      profitMonthFilter: moment().format("YYYY-MM"),
    };
  }

  isSettlementCompleted(payTime) {
    if (!payTime) {return false;}
    const payMoment = moment(payTime, "YYYY-MM-DD HH:mm:ss");
    if (!payMoment.isValid()) {return false;}
    return moment(this.state.now).valueOf() - payMoment.clone().add(48, "hours").valueOf() >= 0;
  }

  renderSettlementInfo(payTime) {
    if (!payTime) {return null;}
    const payMoment = moment(payTime, "YYYY-MM-DD HH:mm:ss");
    if (!payMoment.isValid()) {return null;}
    const deadline = payMoment.clone().add(48, "hours");
    const nowMoment = moment(this.state.now);
    const remainingMs = deadline.valueOf() - nowMoment.valueOf();
    if (remainingMs <= 0) {
      return <Tag color="green" style={{marginLeft: 8}}>结算已完结</Tag>;
    }
    const remainingHours = Math.floor(remainingMs / 3600000);
    const remainingMinutes = Math.floor((remainingMs % 3600000) / 60000);
    return (
      <span style={{marginLeft: 8, fontSize: 12, color: "#faad14"}}>
        完结倒计时: {remainingHours}小时{remainingMinutes}分
      </span>
    );
  }

  componentDidMount() {
    this._timer = setInterval(() => {
      this.setState({now: Date.now()});
    }, 60000);
    // super.componentDidMount();
    this.setState({
      owner: Setting.isAdminUser(this.props.account) ? "admin" : this.props.account.owner,
    });
  }

  componentWillUnmount() {
    if (this._timer) {clearInterval(this._timer);}
  }

  UNSAFE_componentWillMount() {
    const {pagination, filters, sorter} = JSON.parse(localStorage.getItem("providerPage") || "{}");
    if (pagination && filters && sorter) {
      this.setState(
        {
          pagination,
          filters,
          sorter,
        }
      );
      this.handleTableChange(pagination, filters, sorter);
      return;
    }
    const {pagination: p} = this.state;
    this.fetch({pagination: p});
  }

  handleTableChange = (pagination, filters, sorter) => {
    localStorage.setItem("providerPage", JSON.stringify({
      pagination,
      filters,
      sorter,
    }));
    this.fetch({
      sortField: sorter.field,
      sortOrder: sorter.order,
      pagination,
      ...filters,
      searchText: this.state.searchText,
      searchedColumn: this.state.searchedColumn,
    });
  };

  newProvider() {
    const randomName = Setting.getRandomName();
    const owner = Setting.isDefaultOrganizationSelected(this.props.account) ? this.state.owner : Setting.getRequestOrganization(this.props.account);
    return {
      owner: owner,
      name: `provider_${randomName}`,
      createdTime: moment().format(),
      displayName: `New Provider - ${randomName}`,
      category: "OAuth",
      type: "GitHub",
      method: "Normal",
      clientId: "",
      clientSecret: "",
      enableSignUp: true,
      host: "",
      port: 0,
      sort: 0,
      status: true,
      isDefault: false,
      providerUrl: "https://github.com/organizations/xxx/settings/applications/1234567",
    };
  }
  profitSharingFinishByProvider(index) {
    this.setState({profitDoing: true});
    ProviderBackend.profitSharingFinishByProvider(this.state.data[index]).then((res) => {
      this.setState({profitDoing: false});
      if (res.status === "ok") {
        const data = res.data;
        if (data.failedCount > 0) {
          Setting.showMessage("warn", `分账完成: 成功 ${data.successCount} 笔，失败 ${data.failedCount} 笔`);
          this.setState({
            profitStatus: {
              ...this.state.profitStatus,
              successCount: data.successCount,
              successAmount: data.successAmount,
              failedPayments: data.failedPayments,
              pendingCount: Math.max(0, (this.state.profitStatus?.pendingCount || 0) - data.successCount),
              pendingAmount: Math.max(0, (this.state.profitStatus?.pendingAmount || 0) - data.successAmount),
            },
          });
        } else {
          Setting.showMessage("success", i18next.t("general:Successfully profit"));
          if (this.state.selectedProvider) {
            this.showProfitModal(this.state.selectedProvider);
          }
        }
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to profit")}: ${res.msg}`);
      }
    }).catch((error) => {
      this.setState({profitDoing: false});
      Setting.showMessage("error", `分账请求失败: ${error.message}`);
    });
  }
  getMonthRange(monthStr) {
    const start = moment(monthStr, "YYYY-MM").startOf("month").format("YYYY-MM-DD HH:mm:ss");
    const end = moment(monthStr, "YYYY-MM").endOf("month").add(1, "day").format("YYYY-MM-DD HH:mm:ss");
    return {startTime: start, endTime: end};
  }

  showProfitModal(record) {
    const monthStr = this.state.profitMonthFilter;
    const {startTime, endTime} = this.getMonthRange(monthStr);
    this.setState({
      profitModalVisible: true,
      profitLoading: true,
      selectedProvider: record,
      profitStatus: null,
    });
    ProviderBackend.getProfitSharingStatusByProvider(record, startTime, endTime).then((res) => {
      this.setState({
        profitLoading: false,
        profitStatus: res.status === "ok" ? res.data : null,
      });
    }).catch((error) => {
      this.setState({profitLoading: false});
      Setting.showMessage("error", `获取分账状态失败: ${error.message}`);
    });
  }

  onChangeProfitMonth(date, dateStr) {
    if (!date || !dateStr) {return;}
    const monthStr = dateStr;
    if (monthStr === this.state.profitMonthFilter) {return;}
    const {startTime, endTime} = this.getMonthRange(monthStr);
    this.setState({profitMonthFilter: monthStr, profitLoading: true});
    ProviderBackend.getProfitSharingStatusByProvider(this.state.selectedProvider, startTime, endTime).then((res) => {
      this.setState({
        profitLoading: false,
        profitStatus: res.status === "ok" ? res.data : null,
      });
    }).catch((error) => {
      this.setState({profitLoading: false});
      Setting.showMessage("error", `获取分账状态失败: ${error.message}`);
    });
  }
  handleProfitModalClose() {
    this.setState({
      profitModalVisible: false,
      selectedProvider: null,
      profitStatus: null,
    });
  }
  profitSinglePayment(paymentId) {
    ProviderBackend.profitSharingSingle(paymentId).then((res) => {
      if (res.status === "ok") {
        message.success(i18next.t("general:Successfully profit"));
        if (this.state.selectedProvider) {
          this.showProfitModal(this.state.selectedProvider);
        }
      } else {
        message.error(`${i18next.t("general:Failed to profit")}: ${res.msg}`);
      }
    }).catch((error) => {
      message.error(`分账请求失败: ${error.message}`);
    });
  }
  checkPaymentStatus(payment) {
    const paymentName = payment.name;
    const paymentOwner = payment.owner;
    this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: "checking"}});
    const paymentId = paymentOwner + "/" + paymentName;
    ProviderBackend.getPaymentStatusFromWechat(paymentId).then((res) => {
      if (res.status === "ok") {
        const currentFailedPayments = this.state.profitStatus.failedPayments || [];
        const currentPendingPayments = this.state.profitStatus.pendingPayments || [];
        const updatedFailedPayments = currentFailedPayments.map(p => {
          if (p.name === paymentName) {
            return {...p, wechatStatus: res.data};
          }
          return p;
        });
        const updatedPendingPayments = currentPendingPayments.map(p => {
          if (p.name === paymentName) {
            return {...p, wechatStatus: res.data};
          }
          return p;
        });
        this.setState({
          profitStatus: {
            ...this.state.profitStatus,
            failedPayments: updatedFailedPayments,
            pendingPayments: updatedPendingPayments,
          },
          paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null},
        });
        if (res.data.isRefunded) {
          message.warning(`订单${paymentName}已在微信端退款`);
        }
      } else {
        message.error(`查询失败: ${res.msg}`);
        this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      }
    }).catch((error) => {
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      message.error(`查询请求失败: ${error.message}`);
    });
  }
  syncPaymentStatus(payment) {
    const paymentName = payment.name;
    const paymentOwner = payment.owner;
    this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: "syncing"}});
    const paymentId = paymentOwner + "/" + paymentName;
    ProviderBackend.syncPaymentStatusFromWechat(paymentId).then((res) => {
      if (res.status === "ok") {
        message.success(`订单${paymentName}状态已同步`);
        if (this.state.selectedProvider) {
          this.showProfitModal(this.state.selectedProvider);
        }
        this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      } else {
        message.error(`同步失败: ${res.msg}`);
        this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      }
    }).catch((error) => {
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      message.error(`同步请求失败: ${error.message}`);
    });
  }
  checkProfitSharing(payment) {
    const paymentName = payment.name;
    const paymentOwner = payment.owner;
    this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: "checking"}});
    const paymentId = paymentOwner + "/" + paymentName;
    ProviderBackend.checkProfitSharingFromWechat(paymentId).then((res) => {
      if (res.status === "ok") {
        message.success(`核对结果: ${res.data}`);
        if (this.state.selectedProvider) {
          this.showProfitModal(this.state.selectedProvider);
        }
      } else {
        message.error(`核对失败: ${res.msg}`);
      }
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
    }).catch((error) => {
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName]: null}});
      message.error(`核对请求失败: ${error.message}`);
    });
  }
  sumProviderPayment(index) {
    ProviderBackend.sumProviderPayment(this.state.data[index]).then((res) => {
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully profit"));
        // this.handleTableChange();
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to profit")}: ${res.msg}`);
      }
    }).catch((error) => {
      Setting.showMessage("error", `请求失败: ${error.message}`);
    });
  }
  manualVirtualShipping(payment) {
    const callbackUrl = payment.callbackUrl;
    if (!callbackUrl) {
      message.error("该订单无回调地址，无法触发虚拟发货");
      return;
    }
    const baseUrl = callbackUrl.replace(/\/api\/payment\/callback\/?$/, "");
    if (baseUrl === callbackUrl) {
      message.error("无法解析回调地址");
      return;
    }
    const orderId = payment.productDisplayName;
    const transactionId = payment.transactionId;
    if (!orderId) {
      message.error("订单ID为空");
      return;
    }
    const paymentName = payment.name;
    this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName + "_ship"]: true}});
    fetch(`${baseUrl}/api/wechat/virtual-shipping/manual`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({orderId, transactionId}),
    }).then(res => res.json()).then(data => {
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName + "_ship"]: false}});
      if (data.status === 200) {
        message.success(`订单 ${orderId} 虚拟发货成功`);
      } else {
        message.error(`虚拟发货失败: ${data.msg || JSON.stringify(data)}`);
      }
    }).catch(err => {
      this.setState({paymentStatusLoading: {...this.state.paymentStatusLoading, [paymentName + "_ship"]: false}});
      message.error(`虚拟发货请求失败: ${err.message}`);
    });
  }
  addProvider() {
    const newProvider = this.newProvider();
    ProviderBackend.addProvider(newProvider)
      .then((res) => {
        if (res.status === "ok") {
          this.props.history.push({pathname: `/providers/${newProvider.owner}/${newProvider.name}`, mode: "add"});
          Setting.showMessage("success", i18next.t("general:Successfully added"));
        } else {
          Setting.showMessage("error", `${i18next.t("general:Failed to add")}: ${res.msg}`);
        }
      })
      .catch(error => {
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  }

  deleteProvider(i) {
    ProviderBackend.deleteProvider(this.state.data[i])
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("general:Successfully deleted"));
          this.setState({
            data: Setting.deleteRow(this.state.data, i),
            pagination: {total: this.state.pagination.total - 1},
          });
        } else {
          Setting.showMessage("error", `${i18next.t("general:Failed to delete")}: ${res.msg}`);
        }
      })
      .catch(error => {
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  }

  renderTable(providers) {
    const columns = [
      {
        title: i18next.t("general:Name"),
        dataIndex: "name",
        key: "name",
        width: "120px",
        fixed: "left",
        sorter: true,
        defaultFilteredValue: this.state.filters?.["name"] ? this.state.filters?.["name"] : undefined,
        ...this.getColumnSearchProps("name"),
        render: (text, record, index) => {
          return (
            <Link to={`/providers/${record.owner}/${text}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("general:Organization"),
        dataIndex: "owner",
        key: "owner",
        width: "150px",
        sorter: true,
        defaultFilteredValue: this.state.filters?.["owner"] ? this.state.filters?.["owner"] : undefined,
        ...this.getColumnSearchProps("owner"),
        render: (text, record, index) => {
          return (text !== "admin") ? text : i18next.t("provider:admin (Shared)");
        },
      },
      {
        title: i18next.t("general:Created time"),
        dataIndex: "createdTime",
        key: "createdTime",
        width: "180px",
        sorter: true,
        defaultFilteredValue: this.state.filters?.["createdTime"] ? this.state.filters?.["createdTime"] : undefined,
        render: (text, record, index) => {
          return Setting.getFormattedDate(text);
        },
      },
      {
        title: i18next.t("general:Display name"),
        dataIndex: "displayName",
        key: "displayName",
        defaultFilteredValue: this.state.filters?.["displayName"] ? this.state.filters?.["displayName"] : undefined,
        // width: '100px',
        sorter: true,
        ...this.getColumnSearchProps("displayName"),
      },
      {
        title: i18next.t("general:Pay total"),
        dataIndex: "payTotal",
        key: "payTotal",
        defaultFilteredValue: this.state.filters?.["payTotal"] ? this.state.filters?.["payTotal"] : undefined,
        // width: '100px',
        sorter: true,
        ...this.getColumnSearchProps("payTotal"),
      },
      {
        title: i18next.t("general:Wait profit"),
        dataIndex: "waitProfit",
        key: "waitProfit",
        // width: '100px',
        sorter: true,
        defaultFilteredValue: this.state.filters?.["waitProfit"] ? this.state.filters?.["waitProfit"] : undefined,
        ...this.getColumnSearchProps("waitProfit"),
      },
      {
        title: i18next.t("general:Profit finish time"),
        dataIndex: "profitFinishTime",
        key: "profitFinishTime",
        width: "180px",
        sorter: true,
        render: (text, record, index) => {
          return Setting.getFormattedDate(text);
        },
      },
      {
        title: i18next.t("provider:Category"),
        dataIndex: "category",
        key: "category",
        filterMultiple: false,
        filters: [
          {text: "Captcha", value: "Captcha"},
          {text: "Email", value: "Email"},
          {text: "Notification", value: "Notification"},
          {text: "OAuth", value: "OAuth"},
          {text: "Payment", value: "Payment"},
          {text: "SAML", value: "SAML"},
          {text: "SMS", value: "SMS"},
          {text: "Storage", value: "Storage"},
          {text: "Web3", value: "Web3"},
        ],
        defaultFilteredValue: this.state.filters?.["category"] ? this.state.filters?.["category"] : undefined,
        width: "110px",
        sorter: true,
      },
      {
        title: i18next.t("provider:Type"),
        dataIndex: "type",
        key: "type",
        width: "110px",
        align: "center",
        filterMultiple: false,
        filters: [
          {text: "Captcha", value: "Captcha", children: Setting.getProviderTypeOptions("Captcha").map((o) => {return {text: o.id, value: o.name};})},
          {text: "Email", value: "Email", children: Setting.getProviderTypeOptions("Email").map((o) => {return {text: o.id, value: o.name};})},
          {text: "Notification", value: "Notification", children: Setting.getProviderTypeOptions("Notification").map((o) => {return {text: o.id, value: o.name};})},
          {text: "OAuth", value: "OAuth", children: Setting.getProviderTypeOptions("OAuth").map((o) => {return {text: o.id, value: o.name};})},
          {text: "Payment", value: "Payment", children: Setting.getProviderTypeOptions("Payment").map((o) => {return {text: o.id, value: o.name};})},
          {text: "SAML", value: "SAML", children: Setting.getProviderTypeOptions("SAML").map((o) => {return {text: o.id, value: o.name};})},
          {text: "SMS", value: "SMS", children: Setting.getProviderTypeOptions("SMS").map((o) => {return {text: o.id, value: o.name};})},
          {text: "Storage", value: "Storage", children: Setting.getProviderTypeOptions("Storage").map((o) => {return {text: o.id, value: o.name};})},
          {text: "Web3", value: "Web3", children: Setting.getProviderTypeOptions("Web3").map((o) => {return {text: o.id, value: o.name};})},
        ],
        sorter: true,
        render: (text, record, index) => {
          return Provider.getProviderLogoWidget(record);
        },
        defaultFilteredValue: this.state.filters?.["type"] ? this.state.filters?.["type"] : undefined,
      },
      {
        title: i18next.t("provider:Client ID"),
        dataIndex: "clientId",
        key: "clientId",
        width: "100px",
        sorter: true,
        defaultFilteredValue: this.state.filters?.["clientId"] ? this.state.filters?.["clientId"] : undefined,
        ...this.getColumnSearchProps("clientId"),
        render: (text, record, index) => {
          return Setting.getShortText(text);
        },
      },
      {
        title: i18next.t("provider:Provider URL"),
        dataIndex: "providerUrl",
        key: "providerUrl",
        width: "150px",
        sorter: true,
        defaultFilteredValue: this.state.filters?.["providerUrl"] ? this.state.filters?.["providerUrl"] : undefined,
        ...this.getColumnSearchProps("providerUrl"),
        render: (text, record, index) => {
          return (
            <a target="_blank" rel="noreferrer" href={text}>
              {
                Setting.getShortText(text)
              }
            </a>
          );
        },
      },
      {
        title: i18next.t("general:Action"),
        dataIndex: "",
        key: "op",
        width: "170px",
        fixed: (Setting.isMobile()) ? "false" : "right",
        render: (text, record, index) => {
          return (
            <div>
              <Button disabled={!Setting.isAdminUser(this.props.account) && (record.owner !== this.props.account.owner)} style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}} type="primary" onClick={() => this.props.history.push(`/providers/${record.owner}/${record.name}`)}>{i18next.t("general:Edit")}</Button>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}}
                onClick={() => this.sumProviderPayment(index)}>{i18next.t("provider:Sum price")}</Button>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}}
                onClick={() => this.showProfitModal(record)}>{i18next.t("provider:Profit")}</Button>
              <PopconfirmModal
                title={i18next.t("general:Sure to delete") + `: ${record.name} ?`}
                onConfirm={() => this.deleteProvider(index)}
                disabled={!Setting.isAdminUser(this.props.account) && (record.owner !== this.props.account.owner)}
              >
              </PopconfirmModal>
            </div>
          );
        },
      },
    ];

    const paginationProps = {
      total: this.state.pagination.total,
      current: this.state.pagination.current,
      pageSize: this.state.pagination.pageSize,
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: () => i18next.t("general:{total} in total").replace("{total}", this.state.pagination.total),
    };

    return (
      <div>
        <Table scroll={{x: "max-content"}} columns={columns} dataSource={providers} rowKey={(record) => `${record.owner}/${record.name}`} size="middle" bordered pagination={paginationProps}
          title={() => (
            <div>
              {i18next.t("general:Providers")}&nbsp;&nbsp;&nbsp;&nbsp;
              <Button id="add-button" type="primary" size="small" onClick={this.addProvider.bind(this)}>{i18next.t("general:Add")}</Button>
            </div>
          )}
          loading={this.state.loading}
          onChange={this.handleTableChange}
        />
        <Modal
          title={`${i18next.t("provider:Profit Sharing Status")} - ${this.state.selectedProvider?.displayName || this.state.selectedProvider?.name || ""}`}
          open={this.state.profitModalVisible}
          onCancel={() => this.handleProfitModalClose()}
          width={800}
          footer={[
            <Button key="close" onClick={() => this.handleProfitModalClose()}>
              {i18next.t("general:Close")}
            </Button>,
          ]}
        >
          {this.state.profitLoading ? (
            <Spin>{i18next.t("general:Loading...")}</Spin>
          ) : this.state.profitStatus ? (
            <div>
              <div style={{marginBottom: 16}}>
                <DatePicker picker="month" value={moment(this.state.profitMonthFilter, "YYYY-MM")} onChange={(date, dateStr) => this.onChangeProfitMonth(date, dateStr)} style={{marginRight: 16}} allowClear={false} />
                <Tag color="blue">总计: {this.state.profitStatus.totalCount} / ¥{this.state.profitStatus.totalAmount?.toFixed(2)}</Tag>
                <Tag color="green">已分账: {this.state.profitStatus.successCount} / ¥{this.state.profitStatus.successAmount?.toFixed(2)}</Tag>
                <Tag color="orange">待分账: {this.state.profitStatus.pendingCount} / ¥{this.state.profitStatus.pendingAmount?.toFixed(2)}</Tag>
                <div style={{fontSize: 12, color: "#888", marginTop: 8, lineHeight: 1.8}}>
                  支付成功后自动触发微信虚拟发货，订单从支付时间起48小时结算完结。
                  列表显示各订单的结算倒计时，结算完结后方可发起分账。
                </div>
              </div>
              {Array.isArray(this.state.profitStatus.successPayments) && this.state.profitStatus.successPayments.length > 0 && (
                <div style={{marginBottom: 16}}>
                  <h4>已分账订单 (成功支付)</h4>
                  <div style={{maxHeight: 250, overflowY: "auto", border: "1px solid #e8e8e8", borderRadius: 4}}>
                    {this.state.profitStatus.successPayments.map((payment, idx) => (
                      <div key={idx} style={{padding: "8px", borderBottom: "1px solid #f0f0f0", background: "#f6ffed"}}>
                        <div>
                          <strong>{payment.name}</strong> - ¥{payment.price?.toFixed(2)}
                          <Tag color="green" style={{marginLeft: 8}}>已分账</Tag>
                          {payment.callbackState === 1 ? (
                            <Tag color="blue" style={{marginLeft: 8}}>回调成功</Tag>
                          ) : payment.callbackState === -1 ? (
                            <Tag color="red" style={{marginLeft: 8}}>回调失败</Tag>
                          ) : (
                            <Tag style={{marginLeft: 8}}>未回调</Tag>
                          )}
                        </div>
                        {payment.payTime && (
                          <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                            支付时间: {payment.payTime}
                          </div>
                        )}
                        {payment.profitPrice > 0 && (
                          <div style={{fontSize: 12, color: "#52c41a", marginTop: 4}}>
                            分账金额: ¥{payment.profitPrice?.toFixed(2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {this.state.profitStatus.pendingCount > 0 && (
                <div>
                  <div style={{marginBottom: 8}}>
                    <Button type="primary" loading={this.state.profitDoing} onClick={() => this.profitSharingFinishByProvider(this.state.data.findIndex(p => p.owner === this.state.selectedProvider?.owner && p.name === this.state.selectedProvider?.name))}>
                      全部发起分账
                    </Button>
                    <span style={{marginLeft: 16, color: "#888"}}>点击上方按钮对待分账订单发起分账</span>
                  </div>
                  <div style={{marginBottom: 16, fontSize: 12, color: "#888"}}>
                    以下待分账订单可点击&quot;查询状态&quot;查看微信端状态，如已退款请点击&quot;同步状态&quot;
                  </div>
                  {Array.isArray(this.state.profitStatus.pendingPayments) && this.state.profitStatus.pendingPayments.length > 0 && (
                    <div style={{maxHeight: 300, overflowY: "auto", marginBottom: 16}}>
                      {this.state.profitStatus.pendingPayments.map((payment, idx) => (
                        <div key={idx} style={{padding: "8px", borderBottom: "1px solid #f0f0f0", background: "#fffbe6"}}>
                          <div>
                            <strong>{payment.name}</strong> - ¥{payment.price?.toFixed(2)}
                            {payment.wechatStatus?.isRefunded && <Tag color="red" style={{marginLeft: 8}}>已退款</Tag>}
                            <Button size="small" style={{marginLeft: 8}} loading={this.state.paymentStatusLoading[payment.name] === "checking"} onClick={() => this.checkPaymentStatus(payment)}>
                              查询状态
                            </Button>
                            <Button size="small" style={{marginLeft: 8}} onClick={() => this.checkProfitSharing(payment)}>
                              核对分账
                            </Button>
                            {payment.wechatStatus?.isRefunded && (
                              <Button size="small" type="primary" style={{marginLeft: 8}} loading={this.state.paymentStatusLoading[payment.name] === "syncing"} onClick={() => this.syncPaymentStatus(payment)}>
                                同步状态
                              </Button>
                            )}
                          </div>
                          {payment.payTime && (
                            <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                              支付时间: {payment.payTime}
                            </div>
                          )}
                          {payment.wechatStatus && (
                            <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                              微信状态: {payment.wechatStatus.tradeStateMsg}
                              {payment.wechatStatus.refundTime && <span>，退款时间: {payment.wechatStatus.refundTime}</span>}
                              {payment.wechatStatus.successTime && <span>，支付时间: {payment.wechatStatus.successTime}</span>}
                            </div>
                          )}
                          <div style={{fontSize: 12, marginTop: 4}}>
                            {this.renderSettlementInfo(payment.payTime)}
                            {payment.productDisplayName && payment.callbackUrl && !this.isSettlementCompleted(payment.payTime) && (
                              <Button size="small" style={{marginLeft: 8}} loading={this.state.paymentStatusLoading[payment.name + "_ship"] === true} onClick={() => this.manualVirtualShipping(payment)}>
                                发货
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {Array.isArray(this.state.profitStatus.failedPayments) && this.state.profitStatus.failedPayments.length > 0 && (
                <div>
                  <h4>失败列表</h4>
                  <div style={{maxHeight: 300, overflowY: "auto"}}>
                    {this.state.profitStatus.failedPayments.map((payment, idx) => (
                      <div key={idx} style={{padding: "8px", borderBottom: "1px solid #f0f0f0"}}>
                        <div>
                          <strong>{payment.name}</strong> - ¥{payment.price?.toFixed(2)}
                          {payment.wechatStatus?.isRefunded && <Tag color="red" style={{marginLeft: 8}}>已退款</Tag>}
                        </div>
                        <div style={{color: "red", fontSize: 12}}>{payment.errorMsg}</div>
                        {payment.payTime && (
                          <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                            支付时间: {payment.payTime}
                          </div>
                        )}
                        {payment.wechatStatus && (
                          <div style={{fontSize: 12, color: "#888", marginTop: 4}}>
                            微信状态: {payment.wechatStatus.tradeStateMsg}
                            {payment.wechatStatus.refundTime && <span>，退款时间: {payment.wechatStatus.refundTime}</span>}
                            {payment.wechatStatus.successTime && <span>，支付时间: {payment.wechatStatus.successTime}</span>}
                          </div>
                        )}
                        <div style={{fontSize: 12, marginTop: 4}}>
                          {this.renderSettlementInfo(payment.payTime)}
                          {payment.productDisplayName && payment.callbackUrl && !this.isSettlementCompleted(payment.payTime) && (
                            <Button size="small" style={{marginLeft: 8}} loading={this.state.paymentStatusLoading[payment.name + "_ship"] === true} onClick={() => this.manualVirtualShipping(payment)}>
                              发货
                            </Button>
                          )}
                        </div>
                        <div style={{marginTop: 4}}>
                          <Button size="small" loading={this.state.paymentStatusLoading[payment.name] === "checking"} onClick={() => this.checkPaymentStatus(payment)}>
                            查询状态
                          </Button>
                          {payment.wechatStatus?.isRefunded && (
                            <Button size="small" type="primary" style={{marginLeft: 8}} loading={this.state.paymentStatusLoading[payment.name] === "syncing"} onClick={() => this.syncPaymentStatus(payment)}>
                              同步状态
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>{i18next.t("general:No data")}</div>
          )}
        </Modal>
      </div>
    );
  }

  fetch = (params = {}) => {
    let field = params.searchedColumn, value = params.searchText;
    const sortField = params.sortField, sortOrder = params.sortOrder;
    if (params.category !== undefined && params.category !== null) {
      field = "category";
      value = params.category;
    } else if (params.type !== undefined && params.type !== null) {
      field = "type";
      value = params.type;
    }
    this.setState({loading: true});
    (Setting.isDefaultOrganizationSelected(this.props.account) ? ProviderBackend.getGlobalProviders(params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder)
      : ProviderBackend.getProviders(Setting.getRequestOrganization(this.props.account), params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder))
      .then((res) => {
        this.setState({
          loading: false,
        });
        if (res.status === "ok") {
          this.setState({
            data: res.data,
            pagination: {
              ...params.pagination,
              total: res.data2,
            },
            searchText: params.searchText,
            searchedColumn: params.searchedColumn,
          });
        } else {
          if (Setting.isResponseDenied(res)) {
            this.setState({
              isAuthorized: false,
            });
          } else {
            Setting.showMessage("error", res.msg);
          }
        }
      });
  };
}

export default ProviderListPage;
